import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, Phone, Leaf } from 'lucide-react';
import { Button } from './ui/button';

const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location]);

  const navLinks = [
    { name: 'Home', path: '/' },
    { name: 'Services', path: '/services' },
    { name: 'Projects', path: '/projects' },
    { name: 'About', path: '/about' },
    { name: 'Contact', path: '/contact' },
  ];

  const isActive = (path) => location.pathname === path;

  return (
    <header
      data-testid="header"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled ? 'glass-header shadow-md' : 'bg-white/90 backdrop-blur-sm'
      }`}
    >
      <div className="container mx-auto px-4 md:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16 md:h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2" data-testid="logo-link">
            <div className="w-10 h-10 bg-[#2E5C31] rounded-full flex items-center justify-center">
              <Leaf className="w-6 h-6 text-white" />
            </div>
            <span className="text-xl md:text-2xl font-bold text-[#2E5C31] font-['Poppins']">
              DecorVista
            </span>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center gap-8" data-testid="desktop-nav">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                data-testid={`nav-link-${link.name.toLowerCase()}`}
                className={`text-sm font-medium transition-colors duration-200 ${
                  isActive(link.path)
                    ? 'text-[#2E5C31] font-semibold'
                    : 'text-[#4A4A4A] hover:text-[#2E5C31]'
                }`}
              >
                {link.name}
              </Link>
            ))}
          </nav>

          {/* CTA and Phone */}
          <div className="hidden md:flex items-center gap-4">
            <a
              href="tel:+18888808889"
              className="flex items-center gap-2 text-[#2E5C31] font-medium hover:text-[#244A26] transition-colors"
              data-testid="phone-link"
            >
              <Phone className="w-4 h-4" />
              <span className="hidden xl:inline">+1-888-880-8889</span>
            </a>
            <Link to="/contact#estimate-form">
              <Button
                data-testid="header-cta-btn"
                className="bg-[#2E5C31] hover:bg-[#244A26] text-white rounded-full px-6 py-2 font-semibold shadow-md hover:shadow-lg transition-all duration-300"
              >
                Get Free Estimate
              </Button>
            </Link>
          </div>

          {/* Mobile Menu Button */}
          <button
            className="lg:hidden p-2 text-[#2E5C31]"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            data-testid="mobile-menu-btn"
            aria-label="Toggle menu"
          >
            {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      <div
        className={`lg:hidden fixed inset-0 top-16 bg-white z-40 transition-transform duration-300 ${
          isMobileMenuOpen ? 'translate-x-0' : 'translate-x-full'
        }`}
        data-testid="mobile-menu"
      >
        <nav className="flex flex-col p-6 space-y-4">
          {navLinks.map((link) => (
            <Link
              key={link.path}
              to={link.path}
              data-testid={`mobile-nav-link-${link.name.toLowerCase()}`}
              className={`text-lg font-medium py-3 border-b border-gray-100 ${
                isActive(link.path) ? 'text-[#2E5C31]' : 'text-[#4A4A4A]'
              }`}
            >
              {link.name}
            </Link>
          ))}
          <a
            href="tel:+18888808889"
            className="flex items-center gap-3 text-[#2E5C31] font-medium py-3"
            data-testid="mobile-phone-link"
          >
            <Phone className="w-5 h-5" />
            +1-888-880-8889
          </a>
          <Link to="/contact#estimate-form" className="pt-4">
            <Button
              data-testid="mobile-cta-btn"
              className="w-full bg-[#2E5C31] hover:bg-[#244A26] text-white rounded-full py-4 font-semibold"
            >
              Get Free Estimate
            </Button>
          </Link>
        </nav>
      </div>
    </header>
  );
};

export default Header;
